import React, { useState, useEffect, useRef, useCallback } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Flag from 'react-country-flag'; // Make sure it's installed: npm install react-country-flag

const API_URL = "https://api.securxperts.com:8000";

// ====================== COUNTRIES DATA ======================
const COUNTRIES = [
  { code: "+91", name: "India", iso: "IN" },
  { code: "+1", name: "United States", iso: "US" },
  { code: "+44", name: "United Kingdom", iso: "GB" },
  { code: "+61", name: "Australia", iso: "AU" },
  { code: "+971", name: "United Arab Emirates", iso: "AE" },
  { code: "+49", name: "Germany", iso: "DE" },
  { code: "+33", name: "France", iso: "FR" },
  { code: "+81", name: "Japan", iso: "JP" },
];

// ====================== COUNTRY DROPDOWN WITH FLAGS & SEARCH ======================
const CountryDropdown = ({ value, onChange, error, touched }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const dropdownRef = useRef(null);

  const filteredCountries = COUNTRIES.filter(
    (country) =>
      country.code.includes(search) ||
      country.name.toLowerCase().includes(search.toLowerCase())
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedCountry = COUNTRIES.find((c) => c.code === value) || null;

  const handleCountrySelect = (countryCode) => {
    onChange({ target: { name: "country_code", value: countryCode } });
    setIsOpen(false);
    setSearch("");
  };

  return (
    <div className="w-full sm:w-[140px] relative" ref={dropdownRef}>
      <div
        className={`w-full px-4 py-3.5 bg-white/10 border rounded-xl text-white cursor-pointer flex items-center justify-between transition-all duration-200 group hover:border-white/60 ${
          error && touched ? "border-orange-400 border-2 bg-orange-500/10 shadow-orange-500/25" : "border-white/30"
        }`}
        onClick={() => setIsOpen(!isOpen)}
      >
        {selectedCountry ? (
          <div className="flex items-center gap-2">
            <Flag
              countryCode={selectedCountry.iso}
              svg
              style={{
                width: '24px',
                height: '16px',
                borderRadius: '3px',
              }}
            />
            <span className="font-medium">{selectedCountry.code}</span>
          </div>
        ) : (
          <span className="text-white/60">Code</span>
        )}
        <svg 
          className={`w-4 h-4 ml-2 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      {isOpen && (
        <div className="absolute top-full left-0 w-full mt-2 bg-white/95 backdrop-blur-xl border border-white/40 rounded-xl shadow-2xl z-40 max-h-64 overflow-auto">
          <div className="p-3 border-b border-gray-200 sticky top-0 bg-white/95 backdrop-blur-sm">
            <input
              type="text"
              placeholder="Search country or code..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg text-sm text-gray-900 placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:ring-1"
              autoFocus
            />
          </div>
          <div className="py-1">
            {filteredCountries.length > 0 ? (
              filteredCountries.map((country) => (
                <div
                  key={country.code}
                  className="px-4 py-3 cursor-pointer hover:bg-gray-100 transition-colors flex items-center space-x-3 group/item"
                  onClick={() => handleCountrySelect(country.code)}
                >
                  <Flag
                    countryCode={country.iso}
                    svg
                    style={{
                      width: '24px',
                      height: '16px',
                      borderRadius: '3px',
                    }}
                  />
                  <span className="font-medium text-gray-900">{country.code}</span>
                  <span className="text-xs text-gray-600 ml-auto truncate max-w-[100px]">
                    {country.name}
                  </span>
                </div>
              ))
            ) : (
              <div className="px-4 py-6 text-center text-sm text-gray-500">
                No countries found
              </div>
            )}
          </div>
        </div>
      )}

      {error && touched && (
        <p className="mt-1 text-xs text-orange-400 font-medium">{error}</p>
      )}
    </div>
  );
};

// ====================== SUCCESS POPUP with 5-second auto-close ======================
const PopupModal = ({ show, onClose, type, message }) => {
  const timeoutRef = useRef(null);

  useEffect(() => {
    if (show) {
      timeoutRef.current = setTimeout(onClose, 5000);
    }
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [show, onClose]);

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40">
      <div className="relative bg-white rounded-xl shadow-xl p-6 w-full max-w-md mx-4">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-400 hover:text-gray-700 text-lg"
        >
          ×
        </button>

        <div className="text-center">
          {type === "success" && (
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
          )}
          
          <h3
            className={`text-xl font-semibold ${
              type === "success" ? "text-green-600" : "text-red-600"
            }`}
          >
            {type === "success" ? "Success!" : "Error"}
          </h3>

          <p className="mt-3 text-gray-700">
            {message}
          </p>
        </div>
      </div>
    </div>
  );
};

const nameRegex = /^[A-Za-z\s]{3,50}$/;
const onlyLetters = (value) =>
  value.replace(/[^A-Za-z\s]/g, "");

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const phoneRules = {
  "+91": { min: 10, max: 10 }, // India
  "+1": { min: 10, max: 10 },  // USA
  "+44": { min: 10, max: 11 }, // UK
  "+61": { min: 9, max: 9 },   // Australia
  "+971": { min: 9, max: 9 },  // UAE
  "+49": { min: 10, max: 11 }, // Germany
  "+33": { min: 9, max: 10 },  // France
  "+81": { min: 10, max: 10 }, // Japan
};

const yearRegex = /^(19|20)\d{2}$/;

/* ============================================================
   BUSINESS FORM
   ============================================================= */
const BusinessForm = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    country_code: "",
    phoneno: "",
    location: "",
    industry_type: "",
    name_of_business: "",
    poc_name: "",
    product_taking_time: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState({ type: "", message: "" });

  const openModal = (type, message) => {
    setModalData({ type, message });
    setShowModal(true);
    setTimeout(() => {
      setShowModal(false);
    }, 5000);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let updatedValue = value;

    if (["name", "poc_name", "industry_type", "location"].includes(name)) {
      updatedValue = onlyLetters(value);
    }

    if (name === "phoneno") {
      updatedValue = value.replace(/\D/g, "");
    }

    setForm((prev) => ({ ...prev, [name]: updatedValue }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateField = (name, value) => {
    switch (name) {
      case "name":
        if (!value.trim()) return "Name is required";
        if (!nameRegex.test(value)) return "Name must contain only letters (3–50 characters)";
        return "";
      case "email":
        if (!value.trim()) return "Email is required";
        if (!emailRegex.test(value)) return "Enter a valid email address";
        return "";
      case "country_code":
        if (!value) return "Country code is required";
        return "";
      case "phoneno":
        if (!value) return "Phone number is required";
        const rule = phoneRules[form.country_code];
        if (!rule) return "Invalid country code";
        if (value.length < rule.min || value.length > rule.max) {
          return `Phone must be ${rule.min}${rule.min !== rule.max ? "–" + rule.max : ""} digits`;
        }
        return "";
      case "location":
        if (!value.trim()) return "Location is required";
        return "";
      case "name_of_business":
        if (!value.trim()) return "Business name is required";
        return "";
      case "poc_name":
        if (!value.trim()) return "Point of contact is required";
        return "";
      default:
        return "";
    }
  };

  const validate = () => {
    const newErrors = {};
    const fields = ["name", "email", "country_code", "phoneno", "location", "name_of_business", "poc_name"];
    
    // Set all fields as touched when validating on submit
    const newTouched = {};
    fields.forEach(field => {
      newTouched[field] = true;
    });
    setTouched(newTouched);
    
    fields.forEach(field => {
      const error = validateField(field, form[field]);
      if (error) newErrors[field] = error;
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const parseFastAPIError = (errorData) => {
    if (!errorData) return "Unknown error";
    if (errorData.detail) {
      if (Array.isArray(errorData.detail)) {
        return errorData.detail.map((err) => err.msg || "").join(", ");
      }
      return errorData.detail;
    }
    return errorData.message || errorData.error || "Submission failed";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    setSubmitting(true);

    try {
      const payload = {
        name: form.name,
        email: form.email,
        country_code: form.country_code,
        phoneno: form.phoneno,
        location: form.location,
        industry_type: form.industry_type,
        name_of_business: form.name_of_business,
        poc_name: form.poc_name,
        product_taking_time: form.product_taking_time,
      };

      const resp = await fetch(`${API_URL}/forms/lauratek-b2b`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const result = await resp.json();

      if (resp.ok) {
        openModal("success", "Business enquiry submitted successfully!");
        setForm({
          name: "",
          email: "",
          country_code: "",
          phoneno: "",
          location: "",
          industry_type: "",
          name_of_business: "",
          poc_name: "",
          product_taking_time: "",
        });
      } else {
        openModal("error", parseFastAPIError(result));
      }
    } catch {
      openModal("error", "Network error. Please try again.");
    }

    setSubmitting(false);
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    const error = validateField(name, value);
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const handlePhoneChange = (e) => {
    const digits = e.target.value.replace(/\D/g, "");
    const rule = phoneRules[form.country_code];
    if (!rule || digits.length <= rule.max) {
      setForm((prev) => ({ ...prev, phoneno: digits }));
    }
  };

  return (
    <>
      <div className="bg-white/10 backdrop-blur-lg rounded-2xl shadow-2xl border border-white/20 p-6 sm:p-8 md:p-10">
  <h3 className="text-xl sm:text-2xl font-bold text-white text-center mb-6 md:mb-8">
    Business Enquiry
  </h3>
  <form onSubmit={handleSubmit} className="space-y-5 sm:space-y-6">
    <input 
      name="name" 
      value={form.name} 
      onChange={handleChange}
      onBlur={handleBlur}
      maxLength={60}
      placeholder="Full Name *" 
      className="w-full px-5 py-3.5 bg-white/10 border border-white/30 rounded-xl text-white placeholder-white/60 focus:outline-none focus:border-white/60 transition text-sm sm:text-base"
    />
    {errors.name && touched.name && <p className="text-xs text-orange-400 ml-1">{errors.name}</p>}

    <input 
      name="email" 
      type="email" 
      value={form.email} 
      maxLength={60}
      onChange={handleChange}
      onBlur={handleBlur}
      placeholder="Email Address *" 
      className="w-full px-5 py-3.5 bg-white/10 border border-white/30 rounded-xl text-white placeholder-white/60 focus:outline-none focus:border-white/60 transition text-sm sm:text-base"
    />
    {errors.email && touched.email && <p className="text-xs text-orange-400 ml-1">{errors.email}</p>}

    {/* ────────────────────────────────────────────────────────────────
        Changed only this part — now side-by-side only from lg (≥1024px)
    ──────────────────────────────────────────────────────────────── */}
    <div className="flex flex-col lg:flex-row lg:items-start gap-4 lg:gap-3">
      <CountryDropdown 
        value={form.country_code}
        onChange={handleChange}
        error={errors.country_code}
        touched={touched.country_code}
      />
      <input
        name="phoneno"
        value={form.phoneno}
        onChange={handlePhoneChange}
        onBlur={handleBlur}
        placeholder="Phone Number *"
        inputMode="numeric"
        maxLength={10}
        className="flex-1 px-5 py-3.5 bg-white/10 border border-white/30 rounded-xl text-white placeholder-white/60 focus:outline-none focus:border-white/60 transition text-sm sm:text-base min-w-0"
      />
    </div>
    {(errors.phoneno || errors.country_code) && (touched.phoneno || touched.country_code) && (
      <p className="text-xs text-orange-400 ml-1">
        {errors.phoneno || errors.country_code}
      </p>
    )}
    {/* ──────────────────────────────────────────────────────────────── */}

    <input 
      name="location" 
      value={form.location} 
      onChange={handleChange}
      maxLength={60}
      onBlur={handleBlur}
      placeholder="City, Country *" 
      className="w-full px-5 py-3.5 bg-white/10 border border-white/30 rounded-xl text-white placeholder-white/60 focus:outline-none focus:border-white/60 transition text-sm sm:text-base"
    />
    {errors.location && touched.location && <p className="text-xs text-orange-400 ml-1">{errors.location}</p>}

    <input 
      name="industry_type" 
      value={form.industry_type} 
      maxLength={60}
      onChange={handleChange}
      placeholder="Industry Type *" 
      className="w-full px-5 py-3.5 bg-white/10 border border-white/30 rounded-xl text-white placeholder-white/60 focus:outline-none focus:border-white/60 transition text-sm sm:text-base"
    />

    <input 
      name="name_of_business" 
      value={form.name_of_business} 
      maxLength={60}
      onChange={handleChange}
      onBlur={handleBlur}
      placeholder="Business Name *" 
      className="w-full px-5 py-3.5 bg-white/10 border border-white/30 rounded-xl text-white placeholder-white/60 focus:outline-none focus:border-white/60 transition text-sm sm:text-base"
    />
    {errors.name_of_business && touched.name_of_business && <p className="text-xs text-orange-400 ml-1">{errors.name_of_business}</p>}

    <input 
      name="poc_name" 
      value={form.poc_name} 
      maxLength={60}
      onChange={handleChange}
      onBlur={handleBlur}
      placeholder="Point of Contact *" 
      className="w-full px-5 py-3.5 bg-white/10 border border-white/30 rounded-xl text-white placeholder-white/60 focus:outline-none focus:border-white/60 transition text-sm sm:text-base"
    />
    {errors.poc_name && touched.poc_name && <p className="text-xs text-orange-400 ml-1">{errors.poc_name}</p>}

    <input 
      name="product_taking_time" 
      value={form.product_taking_time} 
      maxLength={8}
      onChange={handleChange}
      placeholder="When do you plan to start? *" 
      required
      className="w-full px-5 py-3.5 bg-white/10 border border-white/30 rounded-xl text-white placeholder-white/60 focus:outline-none focus:border-white/60 transition text-sm sm:text-base"
    />
    
    <button
      type="submit"
      disabled={submitting}
      className="w-full bg-gradient-to-r from-[#063852] to-[#0a5c7a] hover:from-[#052d40] hover:to-[#084d66] text-white font-bold py-4 rounded-xl shadow-lg transition transform hover:scale-105 disabled:opacity-70 text-sm sm:text-base"
    >
      {submitting ? "Submitting..." : "Submit Business Enquiry"}
    </button>
  </form>
</div>

      <PopupModal show={showModal} onClose={() => setShowModal(false)} type={modalData.type} message={modalData.message} />
    </>
  );
};

/* ============================================================
   INQUIRY FORM
   ============================================================= */
const InquiryForm = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    country_code: "",
    phoneno: "",
    purpose: "",
    message: "",
    course_type: "",
    product_time: "",
    location: "",
    edu_status: "",
    qualification: "",
    passedout_year: "",
    interest: "",
  });
  
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState({ type: "", message: "" });

  const openModal = (type, message) => {
    setModalData({ type, message });
    setShowModal(true);
    setTimeout(() => {
      setShowModal(false);
    }, 5000);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let updatedValue = value;

    if (["name", "location", "qualification", "purpose", "edu_status", "interest","course_type"].includes(name)) {
      updatedValue = onlyLetters(value);
    }

    if (["phoneno", "passedout_year"].includes(name)) {
      updatedValue = value.replace(/\D/g, "");
    }
    if (name === "phoneno") {
      updatedValue = updatedValue.slice(0, 10);
    }
    if (name === "passedout_year") {
      updatedValue = updatedValue.slice(0, 4);
    }

    // Time fields - allow any characters with maxLength 8
    if (name === "product_time") {
      // No validation - allow any characters
    }

    setForm((prev) => ({
      ...prev,
      [name]: updatedValue,
    }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateField = (name, value) => {
    switch (name) {
      case "name":
        if (!value.trim()) return "Name is required";
        if (!nameRegex.test(value)) return "Name must be 3–50 characters and contain only letters";
        return "";
      case "email":
        if (!value.trim()) return "Email is required";
        if (!emailRegex.test(value)) return "Enter a valid email address";
        return "";
      case "country_code":
        if (!value) return "Country code is required";
        return "";
      case "phoneno":
        if (!value) return "Phone number is required";
        const rule = phoneRules[form.country_code];
        if (!rule) return "Invalid country code";
        if (value.length < rule.min || value.length > rule.max) {
          return `Phone must be ${rule.min}${rule.min !== rule.max ? "–" + rule.max : ""} digits`;
        }
        return "";
      case "course_type":
        if (!value.trim()) return "Interested course is required";
        return "";
      case "location":
        if (!value.trim()) return "Location is required";
        return "";
      case "purpose":
        if (!value.trim()) return "Purpose is required";
        return "";
      case "product_time":
        if (!value.trim()) return "Preferred start time is required";
        if (!/\d/.test(value)) return "Time must include numbers (e.g., 8 pm)";
        if (!/[a-zA-Z]/.test(value)) return "Time must include letters (e.g., am/pm)";
        return "";
      case "edu_status":
        if (!value.trim()) return "Education status is required";
        return "";
      case "qualification":
        if (!value.trim()) return "Qualification is required";
        return "";
      case "passedout_year":
        if (!value.trim()) return "Passed out year is required";
        if (!yearRegex.test(value)) return "Enter a valid year (e.g. 2022)";
        return "";
      case "interest":
        if (!value.trim()) return "Please specify your interest";
        return "";
      case "message":
        if (!value.trim()) return "Message is required";
        if (value.trim().length < 10) return "Message must be at least 10 characters";
        return "";
      default:
        return "";
    }
  };

  const validate = () => {
    const newErrors = {};
    const fields = Object.keys(form);
    
    // Set all fields as touched when validating on submit
    const newTouched = {};
    fields.forEach(field => {
      newTouched[field] = true;
    });
    setTouched(newTouched);
    
    for (const field of fields) {
      const error = validateField(field, form[field]);
      if (error) newErrors[field] = error;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    setSubmitting(true);

    try {
      const payload = {
        name: form.name,
        email: form.email,
        country_code: form.country_code,
        phoneno: form.phoneno,
        purpose: form.purpose,
        message: form.message,
        course_type: form.course_type,
        product_time: form.product_time,
        location: form.location,
        edu_status: form.edu_status,
        qualification: form.qualification,
        passedout_year: form.passedout_year,
        interest: form.interest,
      };

      const resp = await fetch(`${API_URL}/forms/lauratek-b2c`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const result = await resp.json();

      if (resp.ok) {
        openModal("success", "Inquiry Submitted Successfully!");
        setForm({
          name: "",
          email: "",
          country_code: "",
          phoneno: "",
          purpose: "",
          message: "",
          course_type: "",
          product_time: "",
          location: "",
          edu_status: "",
          qualification: "",
          passedout_year: "",
          interest: "",
        });
      } else {
        openModal("error", result.detail || "Submission failed");
      }
    } catch {
      openModal("error", "Network error. Please try again.");
    }

    setSubmitting(false);
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    const error = validateField(name, value);
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const handlePhoneChange = (e) => {
    const digits = e.target.value.replace(/\D/g, "");
    const rule = phoneRules[form.country_code];
    if (!rule || digits.length <= rule.max) {
      setForm((prev) => ({ ...prev, phoneno: digits }));
    }
  };

  return (
    <>
      <div className="bg-white/10 backdrop-blur-lg rounded-2xl shadow-2xl border border-white/20 p-6 sm:p-8 md:p-10">
  <h3 className="text-xl sm:text-2xl font-bold text-white text-center mb-6 md:mb-8">
    Student / Course Inquiry
  </h3>
  <form onSubmit={handleSubmit} className="space-y-5 sm:space-y-6">
    <input 
      name="name" 
      value={form.name} 
      onChange={handleChange}
      onBlur={handleBlur}
      maxLength={60}
      placeholder="Full Name *" 
      className="w-full px-5 py-3.5 bg-white/10 border border-white/30 rounded-xl text-white placeholder-white/60 focus:outline-none focus:border-white/60 transition text-sm sm:text-base"
    />
    {errors.name && touched.name && <p className="text-xs text-orange-400 ml-1">{errors.name}</p>}

    <input 
      name="email" 
      type="email" 
      value={form.email} 
      onChange={handleChange}
      onBlur={handleBlur}
      maxLength={60}
      placeholder="Email Address *" 
      className="w-full px-5 py-3.5 bg-white/10 border border-white/30 rounded-xl text-white placeholder-white/60 focus:outline-none focus:border-white/60 transition text-sm sm:text-base"
    />
    {errors.email && touched.email && <p className="text-xs text-orange-400 ml-1">{errors.email}</p>}

    {/* ────────────────────────────────────────────────────────────────
        Updated only this part — side by side from lg (desktop/laptop)
    ──────────────────────────────────────────────────────────────── */}
    <div className="flex flex-col lg:flex-row lg:items-start gap-4 lg:gap-3">
      <CountryDropdown 
        value={form.country_code}
        onChange={handleChange}
        error={errors.country_code}
        touched={touched.country_code}
      />
      <input 
        name="phoneno" 
        value={form.phoneno} 
        onChange={handlePhoneChange}
        onBlur={handleBlur}
        placeholder="Phone Number *" 
        maxLength={10}
        inputMode="numeric"
        className="flex-1 px-5 py-3.5 bg-white/10 border border-white/30 rounded-xl text-white placeholder-white/60 focus:outline-none focus:border-white/60 transition text-sm sm:text-base min-w-0"
      />
    </div>
    {errors.phoneno && touched.phoneno && <p className="text-xs text-orange-400 ml-1">{errors.phoneno}</p>}
    {errors.country_code && touched.country_code && <p className="text-xs text-orange-400 ml-1">{errors.country_code}</p>}
    {/* ──────────────────────────────────────────────────────────────── */}

    <input 
      name="course_type" 
      value={form.course_type} 
      onChange={handleChange}
      onBlur={handleBlur}
      placeholder="Interested Course *" 
      maxLength={60}
      className="w-full px-5 py-3.5 bg-white/10 border border-white/30 rounded-xl text-white placeholder-white/60 focus:outline-none focus:border-white/60 transition text-sm sm:text-base"
    />
    {errors.course_type && touched.course_type && <p className="text-xs text-orange-400 ml-1">{errors.course_type}</p>}

    <input 
      name="location" 
      value={form.location} 
      onChange={handleChange}
      onBlur={handleBlur}
      maxLength={60}
      placeholder="City, Country *" 
      className="w-full px-5 py-3.5 bg-white/10 border border-white/30 rounded-xl text-white placeholder-white/60 focus:outline-none focus:border-white/60 transition text-sm sm:text-base"
    />
    {errors.location && touched.location && <p className="text-xs text-orange-400 ml-1">{errors.location}</p>}

    <input
      name="purpose"
      value={form.purpose}
      onChange={handleChange}
      maxLength={60}
      onBlur={handleBlur}
      placeholder="Purpose (e.g. Enroll / Info) *"
      className="w-full px-5 py-3.5 bg-white/10 border border-white/30 rounded-xl text-white placeholder-white/60 focus:outline-none focus:border-white/60 transition text-sm sm:text-base"
    />
    {errors.purpose && touched.purpose && <p className="text-xs text-orange-400 ml-1">{errors.purpose}</p>}

    <input
      name="product_time"
      value={form.product_time}
      onChange={handleChange}
      maxLength={8}
      onBlur={handleBlur}
      placeholder="Preferred Start Time *"
      className="w-full px-5 py-3.5 bg-white/10 border border-white/30 rounded-xl text-white placeholder-white/60 focus:outline-none focus:border-white/60 transition text-sm sm:text-base"
    />
    {errors.product_time && touched.product_time && <p className="text-xs text-orange-400 ml-1">{errors.product_time}</p>}

    <input
      name="edu_status"
      value={form.edu_status}
      onChange={handleChange}
      maxLength={60}
      onBlur={handleBlur}
      placeholder="Education Status (Student / Working) *"
      className="w-full px-5 py-3.5 bg-white/10 border border-white/30 rounded-xl text-white placeholder-white/60 focus:outline-none focus:border-white/60 transition text-sm sm:text-base"
    />
    {errors.edu_status && touched.edu_status && <p className="text-xs text-orange-400 ml-1">{errors.edu_status}</p>}

    <input
      name="qualification"
      value={form.qualification}
      onChange={handleChange}
      onBlur={handleBlur}
      maxLength={60}
      placeholder="Qualification *"
      className="w-full px-5 py-3.5 bg-white/10 border border-white/30 rounded-xl text-white placeholder-white/60 focus:outline-none focus:border-white/60 transition text-sm sm:text-base"
    />
    {errors.qualification && touched.qualification && <p className="text-xs text-orange-400 ml-1">{errors.qualification}</p>}

    <input
      name="passedout_year"
      value={form.passedout_year}
      onChange={handleChange}
      onBlur={handleBlur}
      maxLength={4}
      placeholder="Passed Out Year *"
      className="w-full px-5 py-3.5 bg-white/10 border border-white/30 rounded-xl text-white placeholder-white/60 focus:outline-none focus:border-white/60 transition text-sm sm:text-base"
    />
    {errors.passedout_year && touched.passedout_year && <p className="text-xs text-orange-400 ml-1">{errors.passedout_year}</p>}

    <input
      name="interest"
      value={form.interest}
      onChange={handleChange}
      maxLength={60}
      onBlur={handleBlur}
      placeholder="Your Interest (e.g. AI, Data Science, Cybersecurity) *"
      className="w-full px-5 py-3.5 bg-white/10 border border-white/30 rounded-xl text-white placeholder-white/60 focus:outline-none focus:border-white/60 transition text-sm sm:text-base"
    />
    {errors.interest && touched.interest && <p className="text-xs text-orange-400 ml-1">{errors.interest}</p>}

    <textarea 
      name="message" 
      value={form.message} 
      onChange={handleChange}
      onBlur={handleBlur}
      rows={4} 
      placeholder="Your Message *" 
      className="w-full px-5 py-3.5 bg-white/10 border border-white/30 rounded-xl text-white placeholder-white/60 focus:outline-none focus:border-white/60 resize-none text-sm sm:text-base"
    />
    {errors.message && touched.message && <p className="text-xs text-orange-400 ml-1">{errors.message}</p>}

    <button
      type="submit"
      disabled={submitting}
      className="w-full bg-gradient-to-r from-[#063852] to-[#0a5c7a] hover:from-[#052d40] hover:to-[#084d66] text-white font-bold py-4 rounded-xl shadow-lg transition transform hover:scale-105 disabled:opacity-70 text-sm sm:text-base"
    >
      {submitting ? "Submitting..." : "Submit Inquiry"}
    </button>
  </form>
</div>

      <PopupModal show={showModal} onClose={() => setShowModal(false)} type={modalData.type} message={modalData.message} />
    </>
  );
};

// ====================== MAIN COMPONENT ======================
export default function LauratekLMS() {
  const SCROLL_SHOW_THRESHOLD = 300;
  
  const [showScroll, setShowScroll] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isScrolling, setIsScrolling] = useState(false);
  
  const scrollTimeoutRef = useRef(null);
  
  const RADIUS = 49;
  const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
  const dashOffset =
    CIRCUMFERENCE - (scrollProgress / 100) * CIRCUMFERENCE;
  
  const onScroll = useCallback(() => {
    const sc = window.scrollY || window.pageYOffset;
    setShowScroll(sc > SCROLL_SHOW_THRESHOLD);
    
    const doc = document.documentElement;
    const totalScroll = Math.max(0, doc.scrollHeight - doc.clientHeight);
    const progress = totalScroll > 0 ? (sc / totalScroll) * 100 : 0;
    
    setScrollProgress(Math.min(100, Math.max(0, progress)));
    setIsScrolling(true);
    
    if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    
    scrollTimeoutRef.current = setTimeout(() => {
      setIsScrolling(false);
      scrollTimeoutRef.current = null;
    }, 250);
  }, []);
  
  useEffect(() => {
    // Disable browser scroll restoration
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    
    // Force scroll to top on component mount with multiple attempts
    const forceScrollToTop = () => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      if (window.scrollY !== 0) {
        // If still not at top, try again after a short delay
        setTimeout(forceScrollToTop, 50);
      }
    };
    
    forceScrollToTop();
    
    // Additional fallback for stubborn browsers
    setTimeout(forceScrollToTop, 100);
    setTimeout(forceScrollToTop, 300);
    
    onScroll();
    
    window.addEventListener("scroll", onScroll, { passive: true });
    
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
      // Restore scroll restoration when component unmounts
      if ('scrollRestoration' in window.history) {
        window.history.scrollRestoration = 'auto';
      }
    };
  }, []);
  
  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);
  
  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      scrollToTop();
    }
  };

  return (
    <div className="w-full overflow-x-hidden font-sans bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="w-full bg-[#EAF4FF] px-6 md:px-20 pt-2 md:pt-5 pb-5  relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[380px] h-[380px] bg-[#cfe3ff] rounded-full opacity-50 translate-x-24 -translate-y-10"></div>
        <div className="grid md:grid-cols-2 gap-10 items-center relative z-10">
          <div>
            <img src="/img/47.png" alt="Logo" className="w-32 mb-6 md:mt-3" />
            <h1 className="text-4xl font-bold leading-tight text-[#063852] ">
              Next-Gen Online Learning Platform Transforming Education
            </h1>
            <p className="text-[#063852] opacity-70 mb-6">
              Lauratek 2.0 is an advanced online learning management system (LMS) designed to simplify course creation, automate assessments and deliver real-time learner insights. Built for educational institutions, corporates and training organizations, Lauratek 2.0 enables seamless live and on-demand learning through one centralized, intelligent platform.
            </p>
          </div>
          <div className="flex justify-center">
            <img
              src="/img/ukhero.jpg"
              alt="Lauratek 2.0 next-gen online learning platform dashboard"
              className="w-100 md:w-96 rounded-xl md:mt-5"
            />
          </div>
        </div>
      </section>

      {/* Video Section */}
      <section className="w-full py-20 px-6 md:px-0 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl mx-auto mb-10 text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-[#063852]">
              Next-Gen Online Learning Platform Demo
            </h2>
            <p className="text-[#063852] opacity-80 mt-3">
              Lauratek 2.0 streamlines course delivery, assessments,
              certification and learner tracking in one centralised, intelligent
              platform.
            </p>
          </div>
         <div className="relative rounded-2xl md:rounded-3xl overflow-hidden shadow-2xl">
  <video
    className="w-full h-auto max-h-[240px] sm:max-h-[360px] md:max-h-none block"
    src="/video/lauratekv2.oupdated.mp4"
    muted
    playsInline
    autoPlay
    loop
    preload="auto"
    controls
  >
    Your browser does not support HTML5 video.
  </video>
</div>

        </div>
      </section>

      {/* INNOVATIVE LEARNING ENVIRONMENT – UPDATED CONTENT */}
      <section className="w-full py-16 px-6 md:px-20">
        <h2 className="text-4xl font-bold text-[#063852] text-center mb-10">
          Immersive Learning Experience
        </h2>
        <div className="grid md:grid-cols-4 gap-6">
          {[
            {
              img: "/img/uk1.jpg",
              title: "Flexible Course Design",
              text: "Design engaging courses by uploading videos, slides, lessons and learning materials. Structure content into modules and deliver sessions live or on-demand with complete flexibility.",
              alt: "Flexible course design in Lauratek LMS",
            },
            {
              img: "/img/uk2.jpg",
              title: "Automated Assessment Tools",
              text: "Create quizzes, assignments and exams with auto-grading capabilities. Learners receive instant feedback, improving learning outcomes while reducing manual effort.",
              alt: " Automated assessment tools with instant feedback in LMS",
            },
            {
              img: "/img/uk3.png",
              title: "Resource Management Hub",
              text: "Access all PDFs, notes, multimedia files and reference links from one centralized repository, ensuring uninterrupted learning anytime, anywhere.",
              alt: "Centralised learning resources hub in Lauratek 2.0",
            },
            {
              img: "/img/uk4.png",
              title: "Role-Specific Dashboards",
              text: "Dedicated dashboards for administrators, trainers and learners provide relevant insights, controls and reports for efficient learning management.",
              alt: "Role-based dashboards for admin trainer learner in LMS",
            },
          ].map((item, i) => (
            <div
              key={i}
              className="bg-white shadow-lg rounded-xl p-6 text-center border"
            >
              <img
                src={item.img}
                alt={item.alt}
                className="w-12 h-12 mx-auto mb-4"
              />
              <h4 className="font-bold text-[#063852] mb-2">{item.title}</h4>
              <p className="text-sm opacity-70">{item.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* SMART LEARNING MANAGEMENT – UPDATED CONTENT */}
      <section className="w-full py-10 px-6 md:px-20 bg-[#F5FAFF]">
        <h2 className="text-2xl font-bold text-[#063852] text-center mb-8">
          Intelligent Learning Operations for Maximum Efficiency
        </h2>
        <p className="text-center">Lauratek 2.0 automates operational workflows to reduce manual processes and enhance productivity.</p>
        <div className="max-w-3xl mx-auto ">
          <ul className="list-disc list-inside text-[#063852] leading-relaxed space-y-3">
            <li>
              <span className="font-semibold">
                Streamlined Operational Flow:
              </span>{" "}
              Centralized dashboards simplify daily LMS management
            </li>
            <li>
              <span className="font-semibold">End-to-End Learner Oversight:</span>{" "}
              Track progress, attendance, performance and engagement
            </li>
            <li>
              <span className="font-semibold">Robust Platform Capabilities:</span>{" "}
              Real-time analytics, instant certification, exportable reports.
            </li>
            <li>
              <span className="font-semibold">Compliance & Governance Ready:</span>{" "}
              Secure data handling with audit-friendly reporting tools.
            </li>
          </ul>
        </div>
      </section>

      {/* KEY FEATURES OF DIGITAL LEARNING PLATFORM – UPDATED CONTENT */}
      <section className="w-full py-5 px-6 md:px-20">
        <h2 className="text-2xl font-bold text-[#063852] text-center mb-4">
          Explore Advanced Functionalities
        </h2>

        <p className="text-center text-[#063852] opacity-80 max-w-3xl mx-auto mb-10">
          Intelligent e-learning features help educators and organizations design, deliver and manage courses effortlessly. Automated assessments, instant certification, real-time analytics, and centralized resources enhance engagement and efficiency. These tools provide actionable insights for a seamless, high-impact digital learning experience.
        </p>

        <div className="grid md:grid-cols-3 gap-6">
          {[
            {
              img: "/img/l21.png",
              title: "Flexible Course Design",
              text: "Upload lessons, videos, slides and materials easily. Structure content into modules and deliver sessions live or on-demand, adapting to diverse teaching styles efficiently.",
              alt: "Flexible LMS course modules and content delivery",
            },
            {
              img: "/img/l22.png",
              title: "Smart Assessment Engine",
              text: "Create quizzes, assignments and tests that are auto-grade. Learners get immediate feedback, reducing administrative work for educators and streamlining evaluation processes.",
              alt: "Smart assessment engine with auto grading",
            },
            {
              img: "/img/l23.png",
              title: "Automated Credentialing",
              text: "Issue completion certificates automatically. Personalise them with institutional branding to provide professional recognition and standardised acknowledgment for all learners.",
              alt: "Automated certificate generation in Lauratek LMS",
            },
            {
              img: "/img/l24.png",
              title: "Live Learner Analytics",
              text: "Monitor attendance, engagement and progress through intuitive dashboards. Quickly spot learners needing support and adjust teaching strategies accordingly.",
              alt: "Live learner analytics dashboard",
            },
            {
              img: "/img/l25.png",
              title: "Centralised Learning Resources",
              text: "Keep all materials—notes, PDFs, multimedia and reference links—in one accessible repository. Learners can retrieve resources instantly without searching multiple platforms.",
              alt: "Centralised learning resource repository",
            },
            {
              img: "/img/l26.png",
              title: "Role-Based Reporting and Dashboards",
              text: "Provide tailored dashboards for administrators, instructors and learners. Generate performance, attendance and activity reports with a single click for efficient oversight.",
              alt: "Role-based reporting dashboards in LMS",
            },
          ].map((f, i) => (
            <div
              key={i}
              className="bg-white shadow-md border rounded-xl overflow-hidden flex flex-col h-full"
            >
              <img
                src={f.img}
                alt={f.alt}
                title={f.title}
                className="w-full h-48 object-cover"
              />

              <div className="p-5 flex-1">
                <h3 className="font-bold text-[#063852]">{f.title}</h3>
                <p className="text-sm opacity-70 mt-2">{f.text}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Contact Section */}
      <section
        id="contact"
        className="py-20 bg-gradient-to-br from-[#063852] via-[#084d66] to-[#052d40] text-white"
      >
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-extrabold text-white mb-4">
            Get Started with Lauratek 2.0 LMS
          </h2>
          <p className="text-lg text-white/80 max-w-2xl mx-auto mb-12">
            Whether you are a business, institution or student, Lauratek 2.0 provides a powerful and scalable digital learning ecosystem to meet your needs
          </p>

          <div className="grid md:grid-cols-2 gap-10 max-w-5xl mx-auto">
            <BusinessForm />
            <InquiryForm />
          </div>
        </div>
      </section>

      <Footer />
      {/* Scroll to Top Button */}
      {showScroll && (
        <div
          onClick={scrollToTop}
          onKeyDown={handleKeyDown}
          role="button"
          tabIndex={0}
          className="scroll-top fixed bottom-6 right-6 z-50 cursor-pointer 
          flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-r 
          from-blue-500 to-blue-700 shadow-xl hover:scale-110 transition-all"
        >
          <svg className="progress-circle -rotate-90 w-12 h-12 absolute" viewBox="0 0 100 100">
            <circle cx="50" cy="50" r={RADIUS} fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="4" />
            <circle
              cx="50"
              cy="50"
              r={RADIUS}
              fill="none"
              stroke="white"
              strokeWidth="4"
              strokeDasharray={CIRCUMFERENCE}
              strokeDashoffset={dashOffset}
              strokeLinecap="round"
            />
          </svg>

          <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-white relative z-10" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
          </svg>
        </div>
      )}
    </div>
  );
}